import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import {
  addVisitTypeApi,
  getVisitTypeByIdApi,
  updateVisitTypeApi,
} from "../../../services/visitType.service";
import VisitTypeSchema, {
  VISIT_TYPE_OPTIONS,
} from "../../../joi/master/visit-type.schema";

export default function VisitTypeModal({
  open,
  onClose,
  refetch,
  selectedRow = null,
}) {
  const initialState = {
    name: "",
    visitType: "OPD",
    priority: 0,
    new: false,
    followUp: false,
    allowsBackdate: false,
    teleconsultation: false,
    active: true,
  };
  const [isLoading, setIsLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState(initialState);

  const mapRowToFormData = (row) => ({
    ...initialState,
    _id: row?._id,
    name: row?.name || "",
    visitType: VISIT_TYPE_OPTIONS.includes(row?.visitType) ? row.visitType : "OPD",
    priority: row?.priority ?? 0,
    new: Boolean(row?.new),
    followUp: Boolean(row?.followUp),
    allowsBackdate: Boolean(row?.allowsBackdate),
    teleconsultation: Boolean(row?.teleconsultation),
    active: row?.active !== false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevValue) => {
      return {
        ...prevValue,
        [name]:
          type === "checkbox"
            ? checked
            : name === "priority"
            ? Number(value)
            : value,
      };
    });
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadForm = async () => {
      if (!selectedRow?._id) {
        setFormData(initialState);
        return;
      }

      setFormLoading(true);
      try {
        const response = await getVisitTypeByIdApi(selectedRow._id);
        if (cancelled) return;

        const row =
          response?.code === 200 && response?.data
            ? response.data
            : selectedRow;

        setFormData(mapRowToFormData(row));
      } catch {
        if (!cancelled) {
          setFormData(mapRowToFormData(selectedRow));
        }
      } finally {
        if (!cancelled) {
          setFormLoading(false);
        }
      }
    };

    loadForm();

    return () => {
      cancelled = true;
    };
  }, [open, selectedRow?._id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    const { error } = VisitTypeSchema.validate(formData);

    if (error) {
      error.details.forEach((err) => toast.error(err.message));
      return;
    }

    setIsLoading(true);
    if (selectedRow?._id) {
      const { _id, ...updatePayload } = formData;
      response = await updateVisitTypeApi(selectedRow._id, updatePayload);
    } else {
      const { _id, ...createPayload } = formData;
      response = await addVisitTypeApi(createPayload);
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        `Visit Type ${selectedRow?._id ? "updated" : "added"} successfully`
      );
      setFormData(initialState);
      if (typeof refetch === "function") {
        await refetch();
      }
      onClose();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            {selectedRow?._id ? `Edit Visit Type` : `Add New Visit Type`}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          {formLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Name"
                name="name"
                value={formData?.name || ""}
                onChange={handleChange}
                required
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label="Visit Type"
                name="visitType"
                value={formData?.visitType || "OPD"}
                onChange={handleChange}
                required
                fullWidth
                sx={{ bgcolor: "white" }}
              >
                {VISIT_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Priority"
                name="priority"
                value={formData?.priority ?? 0}
                onChange={handleChange}
                type="number"
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  columnGap: { xs: 2, sm: 3 },
                  rowGap: 1,
                  mt: { xs: 0.5, sm: 0 },
                }}
              >
                {[
                  { name: "new", label: "New" },
                  { name: "followUp", label: "Follow Up" },
                  {
                    name: "allowsBackdate",
                    label: "Allows backdated consultation date",
                  },
                  { name: "teleconsultation", label: "Teleconsultation" },
                  { name: "active", label: "Active" },
                ].map(({ name, label }) => (
                  <FormControlLabel
                    key={name}
                    sx={{ m: 0, flex: "0 0 auto" }}
                    componentsProps={{
                      typography: {
                        sx: {
                          whiteSpace: "nowrap",
                          fontSize: "0.9375rem",
                        },
                      },
                    }}
                    control={
                      <Checkbox
                        name={name}
                        checked={Boolean(formData?.[name])}
                        onChange={handleChange}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
          )}
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading || formLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Update"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
